import {
    AuditLog,
    AuditLogDeleteResponse,
    AuditLogDownloadData,
    AuditLogExportAllRequest,
    AuditLogExportAllResponse,
    AuditLogResult,
    DeletionReport,
    DeletionReportsResult,
} from "domain/auditLog";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { ParameterQuery } from "utils/commonFunctions";

const AUDIT_LOGS_PATH = "/api/v1/audit-logs";
const DELETION_REPORTS_PATH = "/api/v1/audit-logs/deletion-reports";
export const AUDIT_LOG_PAGE_SIZE = 100;
export const AUDIT_LOG_EXPORT_LIMIT = 1_000_000;

interface AuditLogQuery {
    usernamePrefix?: string;
    eventType?: string;
    startDate?: string;
    endDate?: string;
    sortOrder?: "ASC" | "DESC";
    page: number;
    size: number;
    includeEventTypes?: boolean;
}

interface AuditLogDeleteRequest {
    uuids: string[];
}

interface AuditLogExportRequest {
    uuids: string[];
}

interface AuditLogDto {
    uuid: string;
    username?: string;
    user_uuid?: string;
    ip?: string;
    license_key?: string;
    action: string;
    event_type: string;
    data?: Record<string, unknown>;
    creation_time: string;
}

interface AuditLogsResponseDto {
    logs: AuditLogDto[];
    total_count: number;
    page: number;
    size: number;
    event_types?: string[];
}

interface AuditLogDeleteResponseDto {
    deletion_report_uuid?: string;
    message: string;
}

function toAuditLog(dto: AuditLogDto): AuditLog {
    return {
        uuid: dto.uuid,
        username: dto.username,
        userUuid: dto.user_uuid,
        ip: dto.ip,
        licenseKey: dto.license_key,
        action: dto.action,
        eventType: dto.event_type,
        data: dto.data,
        creationTime: dto.creation_time,
    };
}

interface AuditLogDownloadDataDto {
    download_url: string;
    expiration: string;
    total_logs: number;
}

export function parseDownloadData(notificationData: string): AuditLogDownloadData | undefined {
    try {
        const dto: AuditLogDownloadDataDto = JSON.parse(notificationData);
        return {
            downloadUrl: dto.download_url,
            expiration: dto.expiration,
            totalLogs: dto.total_logs,
        };
    } catch {
        return undefined;
    }
}

export function extractFilename(disposition: string | null): string {
    if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
            return matches[1].replace(/['"]/g, "");
        }
    }
    return "File path not found.";
}

class AuditLogService {
    public fetchAuditLogs(query: AuditLogQuery, abortController?: AbortController): Promise<AuditLogResult> {
        const queryString = new ParameterQuery()
            .add("username_prefix", query.usernamePrefix)
            .add("event_type", query.eventType)
            .add("start_date", query.startDate)
            .add("end_date", query.endDate)
            .add("sort_order", query.sortOrder ?? "DESC")
            .add("page", String(query.page))
            .add("size", String(query.size))
            .add("include_event_types", query.includeEventTypes ? "true" : undefined)
            .createQueryString();

        return apiGatewayService
            .invokeApi(`${AUDIT_LOGS_PATH}${queryString}`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
            })
            .then((data: AuditLogsResponseDto): AuditLogResult => ({
                logs: data.logs.map(toAuditLog),
                totalCount: data.total_count,
                page: data.page,
                size: data.size,
                eventTypes: data.event_types,
            }));
    }

    public deleteAuditLogs(
        request: AuditLogDeleteRequest,
        abortController?: AbortController
    ): Promise<AuditLogDeleteResponse> {
        return apiGatewayService
            .invokeApi(AUDIT_LOGS_PATH, "DELETE", request, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
            })
            .then((dto: AuditLogDeleteResponseDto): AuditLogDeleteResponse => ({
                deletionReportUuid: dto.deletion_report_uuid ?? undefined,
                message: dto.message,
            }));
    }

    public exportAuditLogs(request: AuditLogExportRequest, abortController?: AbortController): Promise<Response> {
        return apiGatewayService.invokeApi(`${AUDIT_LOGS_PATH}/export`, "POST", request, {
            abortSignal: abortController?.signal,
            apiType: ApiType.OLIVER,
            jsonResponse: false,
            emptyResponse: false,
        });
    }

    public exportAllAuditLogs(
        request: AuditLogExportAllRequest,
        abortController?: AbortController
    ): Promise<AuditLogExportAllResponse> {
        return apiGatewayService.invokeApi(
            `${AUDIT_LOGS_PATH}/export`,
            "POST",
            {
                username_prefix: request.usernamePrefix,
                event_type: request.eventType,
                start_date: request.startDate,
                end_date: request.endDate,
                sort_order: request.sortOrder,
            },
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
            }
        );
    }
}

export const auditLogService = new AuditLogService();

interface DeletionReportDto {
    uuid: string;
    tenant_uuid: string;
    deleted_at: string;
    deleted_by: string;
    log_count: number;
    event_types: string[];
    date_range_start: string;
    date_range_end: string;
}

interface DeletionReportsResponseDto {
    reports: DeletionReportDto[];
    next_cursor?: string;
}

function toDeletionReport(dto: DeletionReportDto): DeletionReport {
    return {
        uuid: dto.uuid,
        tenantUuid: dto.tenant_uuid,
        creationTime: dto.deleted_at,
        username: dto.deleted_by,
        logCount: dto.log_count,
        eventTypes: dto.event_types,
        dateRange: `${dto.date_range_start} - ${dto.date_range_end}`,
    };
}

class DeletionReportsService {
    public fetchDeletionReports(
        startDate?: string,
        endDate?: string,
        sortOrder?: string,
        cursor?: string,
        abortController?: AbortController
    ): Promise<DeletionReportsResult> {
        const queryString = new ParameterQuery()
            .add("start_date", startDate)
            .add("end_date", endDate)
            .add("sort_order", sortOrder)
            .add("cursor", cursor)
            .createQueryString();

        return apiGatewayService
            .invokeApi(`${DELETION_REPORTS_PATH}${queryString}`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
            })
            .then((data: DeletionReportsResponseDto): DeletionReportsResult => ({
                reports: data.reports.map(toDeletionReport),
                nextCursor: data.next_cursor,
            }));
    }

    public downloadDeletionReport(uuid: string, abortController?: AbortController): Promise<Response> {
        return apiGatewayService.invokeApi(`${DELETION_REPORTS_PATH}/${uuid}/download`, "POST", null, {
            abortSignal: abortController?.signal,
            apiType: ApiType.OLIVER,
            jsonResponse: false,
            emptyResponse: false,
        });
    }
}

export const deletionReportsService = new DeletionReportsService();
